import { zodResolver } from "@hookform/resolvers/zod";
import {
	getLivestockSettingsQueryOptions,
	listLivestockVaccinationRulesQueryOptions,
} from "@packages/api-client/hooks/livestockController";
import type { ListLivestockVaccinationRules200 } from "@packages/api-client/types/livestockController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus, Syringe, Timer, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
	useCreateLivestockVaccinationRule,
	useDeleteLivestockVaccinationRule,
	useUpdateLivestockSettings,
	useUpdateLivestockVaccinationRule,
} from "@/api/livestock";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	FormRootMessage,
	useGuardedSubmit,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { ResponsiveFormDialog } from "@/components/ui/responsive-form-dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { SubmitButton } from "@/components/ui/submit-button";
import { SettingsLayout } from "@/routes/_auth/app/_app/org/$orgId/settings/-components/SettingsLayout";
import {
	ANY_SEX,
	RULE_KINDS,
	type RuleEditorFormValues,
	ruleEditorSchema,
	toRuleCreatePayload,
	toRuleEditorFormValues,
	toRuleUpdatePayload,
} from "./-components/ruleEditorSchema";
import {
	THRESHOLD_FIELDS,
	type ThresholdsFormValues,
	thresholdsSchema,
	toThresholdsFormValues,
	toThresholdsPayload,
} from "./-components/thresholdsSchema";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/settings/livestock/",
)({
	component: LivestockSettingsPage,
});

type VaccinationRule = ListLivestockVaccinationRules200[number];

function LivestockSettingsPage() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();

	return (
		<SettingsLayout
			activeTab="livestock"
			title={t("livestock.settings.title")}
			testId="livestock-settings-container"
			contentTestId="livestock-settings-content"
		>
			<div className="space-y-3 md:space-y-4">
				<ThresholdsCard orgId={orgId} />
				<VaccinationRulesCard orgId={orgId} />
			</div>
		</SettingsLayout>
	);
}

const THRESHOLDS_FORM_ID = "livestock-thresholds-form";

/**
 * Attention thresholds + carcass yield. Values come back row-or-defaults, so
 * the form is always populated; saving materializes the org row.
 */
function ThresholdsCard({ orgId }: { orgId: string }) {
	const { t } = useTranslation();
	const { data: settings, isLoading } = useQuery(
		getLivestockSettingsQueryOptions(orgId),
	);
	const updateSettings = useUpdateLivestockSettings();

	const form = useForm<ThresholdsFormValues>({
		resolver: zodResolver(thresholdsSchema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: {
			weighingOverdueDays: "",
			paddockOverstayDefaultDays: "",
			calvingWindowDays: "",
			carcassYieldPercent: "",
			dryMatterIntakePercent: "",
			waterIntakePercent: "",
		},
	});
	const { control, reset } = form;

	useEffect(() => {
		if (settings) reset(toThresholdsFormValues(settings));
	}, [settings, reset]);

	const guardedSubmit = useGuardedSubmit(form, (values) =>
		updateSettings.mutateAsync({
			organizationId: orgId,
			data: toThresholdsPayload(values),
		}),
	);

	return (
		<Card>
			<CardHeader className="pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
				<CardTitle className="flex items-center gap-2 text-sm md:text-base">
					<Timer className="w-4 h-4 md:w-5 md:h-5" />
					{t("livestock.settings.thresholds.title")}
				</CardTitle>
			</CardHeader>
			<CardContent className="px-3 md:px-6 pb-3 md:pb-6">
				{isLoading ? (
					<Skeleton className="h-32 w-full" />
				) : (
					<Form {...form}>
						<form
							id={THRESHOLDS_FORM_ID}
							onSubmit={guardedSubmit}
							className="space-y-4"
						>
							<div className="grid gap-4 sm:grid-cols-2">
								{THRESHOLD_FIELDS.map((field) => (
									<FormField
										key={field.key}
										control={control}
										name={field.key}
										render={({ field: rhfField }) => (
											<FormItem>
												<FormLabel required={field.required}>
													{t(
														`livestock.settings.thresholds.${field.key}.label`,
													)}
												</FormLabel>
												<FormControl>
													<NumberField
														mode={
															field.key === "dryMatterIntakePercent" ||
															field.key === "waterIntakePercent"
																? "decimal"
																: "numeric"
														}
														value={rhfField.value}
														onChange={rhfField.onChange}
														onBlur={rhfField.onBlur}
														name={rhfField.name}
														placeholder={
															field.required
																? undefined
																: t(
																		`livestock.settings.thresholds.${field.key}.placeholder`,
																	)
														}
													/>
												</FormControl>
												<p className="text-xs text-base-content/60">
													{t(`livestock.settings.thresholds.${field.key}.help`)}
												</p>
												<FormMessage />
											</FormItem>
										)}
									/>
								))}
							</div>
							<FormRootMessage />
							<div className="flex justify-end">
								<SubmitButton
									form={THRESHOLDS_FORM_ID}
									pending={updateSettings.isPending}
								>
									{t("common.save")}
								</SubmitButton>
							</div>
						</form>
					</Form>
				)}
			</CardContent>
		</Card>
	);
}

/** Sanitary-calendar rules driving the VACCINATION_DUE attention category. */
function VaccinationRulesCard({ orgId }: { orgId: string }) {
	const { t } = useTranslation();
	const { data: rules, isLoading } = useQuery(
		listLivestockVaccinationRulesQueryOptions(orgId),
	);
	const deleteRule = useDeleteLivestockVaccinationRule();

	const [editorOpen, setEditorOpen] = useState(false);
	const [editing, setEditing] = useState<VaccinationRule | null>(null);
	const [pendingDelete, setPendingDelete] = useState<VaccinationRule | null>(
		null,
	);

	const openCreate = () => {
		setEditing(null);
		setEditorOpen(true);
	};
	const openEdit = (rule: VaccinationRule) => {
		setEditing(rule);
		setEditorOpen(true);
	};

	const ruleSummary = (rule: VaccinationRule) =>
		rule.ruleKind === "RECURRING"
			? t("livestock.settings.rules.summaryRecurring", {
					days: rule.intervalDays ?? 0,
				})
			: t("livestock.settings.rules.summaryAgeWindow", {
					min: rule.minAgeDays ?? 0,
					max: rule.maxAgeDays ?? 0,
				});

	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2 md:pb-3 px-3 md:px-6 pt-3 md:pt-6">
				<CardTitle className="flex items-center gap-2 text-sm md:text-base">
					<Syringe className="w-4 h-4 md:w-5 md:h-5" />
					{t("livestock.settings.rules.title")}
				</CardTitle>
				<Button size="sm" onClick={openCreate}>
					<Plus className="mr-1.5 h-4 w-4" />
					{t("livestock.settings.rules.add")}
				</Button>
			</CardHeader>
			<CardContent className="px-3 md:px-6 pb-3 md:pb-6">
				{isLoading ? (
					<Skeleton className="h-24 w-full" />
				) : (rules ?? []).length === 0 ? (
					<p className="py-4 text-sm text-base-content/60">
						{t("livestock.settings.rules.empty")}
					</p>
				) : (
					<ul className="divide-y divide-base-200">
						{(rules ?? []).map((rule) => (
							<li
								key={rule.id}
								className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5"
							>
								<div className="min-w-0 flex-1">
									<p className="flex items-center gap-2 text-sm font-medium text-base-content">
										{rule.name}
										{!rule.isActive && (
											<Badge variant="secondary">{t("common.inactive")}</Badge>
										)}
										{rule.appliesToSex && (
											<Badge variant="outline">
												{t(`livestock.settings.rules.sex.${rule.appliesToSex}`)}
											</Badge>
										)}
									</p>
									<p className="truncate text-xs text-base-content/60">
										{ruleSummary(rule)} ·{" "}
										{t("livestock.settings.rules.matchLabel", {
											match: rule.vaccineMatch,
										})}
									</p>
								</div>
								<Button
									variant="ghost"
									size="icon"
									aria-label={t("common.edit")}
									onClick={() => openEdit(rule)}
								>
									<Pencil className="h-4 w-4" />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									aria-label={t("common.delete")}
									onClick={() => setPendingDelete(rule)}
								>
									<Trash2 className="h-4 w-4 text-error" />
								</Button>
							</li>
						))}
					</ul>
				)}
			</CardContent>

			<RuleEditorDialog
				orgId={orgId}
				open={editorOpen}
				onOpenChange={setEditorOpen}
				rule={editing}
			/>

			<ConfirmDeleteDialog
				open={!!pendingDelete}
				title={t("livestock.settings.rules.deleteConfirm.title", {
					name: pendingDelete?.name ?? "",
				})}
				description={t("livestock.settings.rules.deleteConfirm.body")}
				onCancel={() => setPendingDelete(null)}
				onConfirm={() => {
					if (!pendingDelete) return;
					deleteRule.mutate({
						organizationId: orgId,
						ruleId: pendingDelete.id,
					});
					setPendingDelete(null);
				}}
				isPending={deleteRule.isPending}
			/>
		</Card>
	);
}

const RULE_FORM_ID = "vaccination-rule-form";

/** Create/edit dialog — one form, kind-conditional fields (design §4.1). */
function RuleEditorDialog({
	orgId,
	open,
	onOpenChange,
	rule,
}: {
	orgId: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	rule: VaccinationRule | null;
}) {
	const { t } = useTranslation();
	const createRule = useCreateLivestockVaccinationRule();
	const updateRule = useUpdateLivestockVaccinationRule();

	const form = useForm<RuleEditorFormValues>({
		resolver: zodResolver(ruleEditorSchema),
		mode: "onTouched",
		reValidateMode: "onChange",
		defaultValues: toRuleEditorFormValues(rule),
	});
	const { control, reset, watch } = form;

	// Re-seed the form whenever the dialog opens for a different rule (or blank).
	useEffect(() => {
		if (open) reset(toRuleEditorFormValues(rule));
	}, [open, rule, reset]);

	const recurring = watch("ruleKind") === "RECURRING";
	const isPending = createRule.isPending || updateRule.isPending;

	const guardedSubmit = useGuardedSubmit(form, (values) => {
		const onDone = () => onOpenChange(false);
		if (rule) {
			return updateRule
				.mutateAsync({
					organizationId: orgId,
					ruleId: rule.id,
					data: toRuleUpdatePayload(values),
				})
				.then(onDone);
		}
		return createRule
			.mutateAsync({ organizationId: orgId, data: toRuleCreatePayload(values) })
			.then(onDone);
	});

	return (
		<ResponsiveFormDialog
			open={open}
			onOpenChange={onOpenChange}
			pending={isPending}
			title={
				rule
					? t("livestock.settings.rules.editTitle")
					: t("livestock.settings.rules.addTitle")
			}
			footer={
				<>
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={isPending}
					>
						{t("common.cancel")}
					</Button>
					<SubmitButton form={RULE_FORM_ID} pending={isPending}>
						{t("common.save")}
					</SubmitButton>
				</>
			}
		>
			<Form {...form}>
				<form id={RULE_FORM_ID} onSubmit={guardedSubmit} className="grid gap-4">
					<FormField
						control={control}
						name="name"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("livestock.settings.rules.fields.name")}
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										placeholder={t(
											"livestock.settings.rules.fields.namePlaceholder",
										)}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={control}
						name="vaccineMatch"
						render={({ field }) => (
							<FormItem>
								<FormLabel required>
									{t("livestock.settings.rules.fields.vaccineMatch")}
								</FormLabel>
								<FormControl>
									<Input
										{...field}
										placeholder={t(
											"livestock.settings.rules.fields.vaccineMatchPlaceholder",
										)}
									/>
								</FormControl>
								<p className="text-xs text-base-content/60">
									{t("livestock.settings.rules.fields.vaccineMatchHelp")}
								</p>
								<FormMessage />
							</FormItem>
						)}
					/>
					<div className="grid gap-4 sm:grid-cols-2">
						<FormField
							control={control}
							name="ruleKind"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>
										{t("livestock.settings.rules.fields.ruleKind")}
									</FormLabel>
									<Select value={field.value} onValueChange={field.onChange}>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{RULE_KINDS.map((kind) => (
												<SelectItem key={kind} value={kind}>
													{t(`livestock.settings.rules.kinds.${kind}`)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={control}
							name="appliesToSex"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t("livestock.settings.rules.fields.appliesToSex")}
									</FormLabel>
									<Select value={field.value} onValueChange={field.onChange}>
										<FormControl>
											<SelectTrigger className="w-full">
												<SelectValue />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											<SelectItem value={ANY_SEX}>
												{t("livestock.settings.rules.sex.ANY")}
											</SelectItem>
											<SelectItem value="FEMALE">
												{t("livestock.settings.rules.sex.FEMALE")}
											</SelectItem>
											<SelectItem value="MALE">
												{t("livestock.settings.rules.sex.MALE")}
											</SelectItem>
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					{recurring ? (
						<FormField
							control={control}
							name="intervalDays"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>
										{t("livestock.settings.rules.fields.intervalDays")}
									</FormLabel>
									<FormControl>
										<NumberField
											mode="numeric"
											value={field.value}
											onChange={field.onChange}
											onBlur={field.onBlur}
											name={field.name}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					) : (
						<div className="grid gap-4 sm:grid-cols-2">
							<FormField
								control={control}
								name="minAgeDays"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>
											{t("livestock.settings.rules.fields.minAgeDays")}
										</FormLabel>
										<FormControl>
											<NumberField
												mode="numeric"
												value={field.value}
												onChange={field.onChange}
												onBlur={field.onBlur}
												name={field.name}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={control}
								name="maxAgeDays"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>
											{t("livestock.settings.rules.fields.maxAgeDays")}
										</FormLabel>
										<FormControl>
											<NumberField
												mode="numeric"
												value={field.value}
												onChange={field.onChange}
												onBlur={field.onBlur}
												name={field.name}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					)}
					<FormRootMessage />
				</form>
			</Form>
		</ResponsiveFormDialog>
	);
}
